import { InternalCategoryFeature } from '../../../types/InternalCategory'
import { InternalConcept, InternalTag } from '../../../types/InternalTag'
import { ConceptRow, FeatureRow } from '../components/ScreenshotFeatureList/ScreenshotFeatureList'
import {
  AnalysisScreenshotItem,
  AnalysisScreenshotView,
  ConceptMatchGroup,
  ConceptsReadBack,
  ScreenshotConcept,
  ScreenshotConceptLinks,
  ScreenshotFeature,
  ScreenshotsReadBack,
} from '../types/AnalysisScreenshots'

type StoredScreenshot = {
  screenshotId?: string | null
  url?: string | null
  features?: Record<string, number> | null
  conceptIds?: string[] | null
  commentId?: string | null
  commentPublished?: boolean | null
}

const unique = (ids: string[]) => Array.from(new Set(ids))

// Every write sends the stored item with only the change applied, so comments, the published flag and concept ids no
// concept has any more survive it.
export const toScreenshotItem = (stored: StoredScreenshot): AnalysisScreenshotItem => ({
  screenshotId: stored.screenshotId ?? '',
  url: stored.url ?? '',
  features: { ...(stored.features ?? {}) },
  conceptIds: unique(stored.conceptIds ?? []),
  commentId: stored.commentId ?? null,
  commentPublished: stored.commentPublished ?? false,
})

export const toScreenshotItems = (screenshots?: StoredScreenshot[] | null) => (screenshots ?? []).map(toScreenshotItem)

export const toNewScreenshotItem = (upload: { id: string; url: string }): AnalysisScreenshotItem => ({
  screenshotId: upload.id,
  url: upload.url,
  features: {},
  conceptIds: [],
  commentId: null,
  commentPublished: false,
})

const sameFeatures = (a: Record<string, number>, b: Record<string, number>) => {
  const keys = Object.keys(a)
  return keys.length === Object.keys(b).length && keys.every((key) => b[key] === a[key])
}

const sameIdSet = (a: string[], b: string[]) => {
  const set = new Set(b)
  return a.length === set.size && a.every((id) => set.has(id))
}

export const sameScreenshotItem = (a: AnalysisScreenshotItem, b: AnalysisScreenshotItem) =>
  a.screenshotId === b.screenshotId &&
  a.url === b.url &&
  sameFeatures(a.features, b.features) &&
  sameIdSet(unique(a.conceptIds), b.conceptIds) &&
  a.commentId === b.commentId &&
  a.commentPublished === b.commentPublished

export const sameScreenshotLists = (a: AnalysisScreenshotItem[], b: AnalysisScreenshotItem[]) =>
  a.length === b.length && a.every((item, index) => sameScreenshotItem(item, b[index]))

export const getScreenshotsReadBack = (sent: AnalysisScreenshotItem[], stored: AnalysisScreenshotItem[]): ScreenshotsReadBack => {
  const storedById = new Map(stored.map((item) => [item.screenshotId, item]))
  const sentIds = new Set(sent.map(({ screenshotId }) => screenshotId))
  const commonSent = sent.filter(({ screenshotId }) => storedById.has(screenshotId)).map(({ screenshotId }) => screenshotId)
  const commonStored = stored.filter(({ screenshotId }) => sentIds.has(screenshotId)).map(({ screenshotId }) => screenshotId)
  return {
    missingIds: sent.filter(({ screenshotId }) => !storedById.has(screenshotId)).map(({ screenshotId }) => screenshotId),
    unexpectedIds: stored.filter(({ screenshotId }) => !sentIds.has(screenshotId)).map(({ screenshotId }) => screenshotId),
    orderChanged: commonSent.some((id, index) => commonStored[index] !== id),
    changedIds: sent
      .filter((item) => {
        const storedItem = storedById.get(item.screenshotId)
        return storedItem && !sameScreenshotItem(item, storedItem)
      })
      .map(({ screenshotId }) => screenshotId),
  }
}

export const isReadBackClean = ({ missingIds, unexpectedIds, orderChanged, changedIds }: ScreenshotsReadBack) =>
  !missingIds.length && !unexpectedIds.length && !orderChanged && !changedIds.length

// `expectMatched`: after a match every pair should be stored, after an unmatch none.
export const getConceptsReadBack = (groups: ConceptMatchGroup[], stored: ScreenshotConceptLinks[], expectMatched: boolean): ConceptsReadBack => {
  const storedById = new Map(stored.map((item) => [item.screenshotId, new Set(item.conceptIds)]))
  const notStored: Record<string, string[]> = {}
  groups.forEach(({ screenshotsIds, conceptsIds }) =>
    screenshotsIds.forEach((screenshotId) => {
      const conceptIds = storedById.get(screenshotId) ?? new Set<string>()
      const wrong = conceptsIds.filter((conceptId) => conceptIds.has(conceptId) !== expectMatched)
      if (wrong.length) notStored[screenshotId] = unique([...(notStored[screenshotId] ?? []), ...wrong])
    })
  )
  return { notStored }
}

const featureName = (feature: InternalCategoryFeature) => {
  const names = feature.names as Record<string, string | undefined> | undefined
  return feature.name || names?.en || `#${feature.legacyId}`
}

// Every feature of the answered categories, by legacy id. A screenshot's `{legacyId: weightId}` resolves through it.
export const toFeaturesByLegacyId = (features: InternalCategoryFeature[]) => new Map(features.map((feature) => [feature.legacyId, feature]))

const toScreenshotFeature = (legacyId: number, weightId: number, feature?: InternalCategoryFeature): ScreenshotFeature => ({
  legacyId,
  name: feature ? featureName(feature) : `#${legacyId}`,
  weightId,
  choice: feature?.choices?.find((choice) => choice.weightId === weightId)?.choice ?? String(weightId),
})

// The analysis' answers in their stored order, each with the answered choice: what the dialog offers to add.
export const getAnswerFeatures = (answers: Record<string, Record<string, number>> | null | undefined, features: InternalCategoryFeature[]) => {
  const byId = new Map(features.map((feature) => [feature.id, feature]))
  return Object.values(answers ?? {}).flatMap((categoryAnswers) =>
    Object.entries(categoryAnswers).flatMap(([featureId, weightId]) => {
      const feature = byId.get(featureId)
      return feature ? [toScreenshotFeature(feature.legacyId, weightId, feature)] : []
    })
  )
}

export const toConceptsById = (concepts: InternalConcept[], tags: InternalTag[]) => {
  const tagsById = new Map(tags.map((tag) => [tag.id, tag]))
  return new Map<string, ScreenshotConcept>(
    concepts.map((concept) => {
      const tag = concept.tags.map((id) => tagsById.get(id)).find(Boolean)
      return [concept.id, { id: concept.id, name: tag?.name ?? `#${concept.conceptId}`, featureTagId: concept.featureTagId ?? null }]
    })
  )
}

export const toScreenshotView = (
  item: AnalysisScreenshotItem,
  index: number,
  featuresByLegacyId: Map<number, InternalCategoryFeature>,
  conceptsById: Map<string, ScreenshotConcept>
): AnalysisScreenshotView => ({
  item,
  index,
  features: Object.entries(item.features).map(([legacyId, weightId]) =>
    toScreenshotFeature(Number(legacyId), weightId, featuresByLegacyId.get(Number(legacyId)))
  ),
  concepts: item.conceptIds.flatMap((id) => {
    const concept = conceptsById.get(id)
    return concept ? [concept] : []
  }),
  unknownConceptIds: item.conceptIds.filter((id) => !conceptsById.has(id)),
})

export const isUntagged = (view: AnalysisScreenshotView) => view.features.length === 0

export const matchesScreenshotSearch = (view: AnalysisScreenshotView, text: string) => {
  const search = text.trim().toLowerCase()
  if (!search) return true
  return [view.item.url, ...view.features.flatMap(({ name, choice }) => [name, choice]), ...view.concepts.map(({ name }) => name)].some((value) =>
    value.toLowerCase().includes(search)
  )
}

export const moveScreenshot = <T>(items: T[], index: number, delta: -1 | 1) => {
  const target = index + delta
  if (index < 0 || target < 0 || index >= items.length || target >= items.length) return items
  const moved = [...items]
  ;[moved[index], moved[target]] = [moved[target], moved[index]]
  return moved
}

// Null unless every index is a whole number within the list, given once.
export const parseIndices = (value: string | null, count: number): number[] | null => {
  const parts = (value ?? '').split(',').filter((part) => part !== '')
  if (!parts.length || parts.some((part) => !/^\d+$/.test(part))) return null
  const indices = parts.map(Number)
  if (new Set(indices).size !== indices.length || indices.some((index) => index >= count)) return null
  return indices
}

export const addFeatures = (item: AnalysisScreenshotItem, features: ScreenshotFeature[]): AnalysisScreenshotItem => {
  const added = features.filter(({ legacyId }) => !(String(legacyId) in item.features))
  if (!added.length) return item
  return { ...item, features: { ...item.features, ...Object.fromEntries(added.map(({ legacyId, weightId }) => [String(legacyId), weightId])) } }
}

export const removeFeature = (item: AnalysisScreenshotItem, legacyId: number): AnalysisScreenshotItem => {
  if (!(String(legacyId) in item.features)) return item
  const features = { ...item.features }
  delete features[String(legacyId)]
  return { ...item, features }
}

export const replaceItems = (items: AnalysisScreenshotItem[], replacements: AnalysisScreenshotItem[]) => {
  const byId = new Map(replacements.map((item) => [item.screenshotId, item]))
  return items.map((item) => byId.get(item.screenshotId) ?? item)
}

// Each concept goes to the screenshots that lack it; concepts going to the same screenshots share one call.
export const getConceptMatchGroups = (items: ScreenshotConceptLinks[], conceptIds: string[]): ConceptMatchGroup[] => {
  const groups = new Map<string, ConceptMatchGroup>()
  unique(conceptIds).forEach((conceptId) => {
    const screenshotsIds = items.filter((item) => !item.conceptIds.includes(conceptId)).map(({ screenshotId }) => screenshotId)
    if (!screenshotsIds.length) return
    const key = screenshotsIds.join(',')
    const group = groups.get(key)
    if (group) group.conceptsIds.push(conceptId)
    else groups.set(key, { screenshotsIds, conceptsIds: [conceptId] })
  })
  return Array.from(groups.values())
}

export const getConceptUnmatchGroup = (items: ScreenshotConceptLinks[], conceptId: string): ConceptMatchGroup | undefined => {
  const screenshotsIds = items.filter((item) => item.conceptIds.includes(conceptId)).map(({ screenshotId }) => screenshotId)
  return screenshotsIds.length ? { screenshotsIds, conceptsIds: [conceptId] } : undefined
}

type FeatureListView = Pick<AnalysisScreenshotView, 'features' | 'concepts' | 'unknownConceptIds'>

const featureKey = ({ legacyId, weightId }: ScreenshotFeature) => `${legacyId}:${weightId}`

// The stored features and concepts of the edited screenshots, each once, with how many of them have it. With several, the
// hovered one's are marked.
export const toStoredFeatureRows = <T extends FeatureListView>(views: T[], hovered?: T) => {
  const features = new Map<string, FeatureRow>()
  const concepts = new Map<string, ConceptRow>()
  views.forEach((view) => {
    view.features.forEach((feature) => {
      const row = features.get(featureKey(feature)) ?? { feature, count: 0 }
      features.set(featureKey(feature), { ...row, count: (row.count ?? 0) + 1 })
    })
    view.concepts.forEach((concept) => {
      const row = concepts.get(concept.id) ?? { concept, count: 0 }
      concepts.set(concept.id, { ...row, count: (row.count ?? 0) + 1 })
    })
  })
  const mark = <R extends FeatureRow | ConceptRow>(row: R, has: (view: T) => boolean): R =>
    views.length > 1 ? { ...row, marked: hovered ? has(hovered) : undefined } : row
  return {
    features: Array.from(features.values()).map((row) => mark(row, (view) => view.features.some((feature) => featureKey(feature) === featureKey(row.feature)))),
    concepts: Array.from(concepts.values()).map((row) => mark(row, (view) => view.concepts.some(({ id }) => id === row.concept.id))),
    unknownConceptIds: unique(views.flatMap((view) => view.unknownConceptIds)),
  }
}

// The 800 px version the image service stores next to each screenshot.
export const getThumbnailUrl = (url: string) => {
  const dot = url.lastIndexOf('.')
  return dot > url.lastIndexOf('/') ? `${url.slice(0, dot)}-800${url.slice(dot)}` : url
}

// Legacy's key and value, so both apps return to the same screenshot.
export const lastViewedScreenshotKey = 'GR_LAST_VIEWED_SCREENSHOT_GAME_INDEX'

export const readLastViewedIndex = (gameId: string): number | undefined => {
  try {
    const stored = JSON.parse(window.localStorage.getItem(lastViewedScreenshotKey) ?? 'null') as { gameId?: string; index?: number } | null
    return stored?.gameId === gameId && typeof stored.index === 'number' ? stored.index : undefined
  } catch {
    return undefined
  }
}

export const writeLastViewedIndex = (gameId: string, index: number) => {
  try {
    window.localStorage.setItem(lastViewedScreenshotKey, JSON.stringify({ gameId, index }))
  } catch {}
}
